import { Box } from "@mui/material";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NormalizedNurseMenu } from "@/components/NormalizedNurseMenu/NormalizedNurseMenu";
import { StaffingChanges } from "@/components/StaffingChanges/StaffingChanges";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useUserSettingsContext } from "@/contexts/Staffing/UserSettingsContext";
import { useWeekViewContext } from "@/contexts/WeekView";
import { weekPersonIdsOf } from "@/contexts/WeekView/weekPersonIds";
import { useFormatPersonName } from "@/hooks/useNameFormat";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { VirtualizedList } from "@/ui/molecules/VirtualizedList";
import { slotWindowOnDay } from "@/utils/dateUtils";
import { ChangesSource, type NurseInShift } from "@/utils/models";
import {
	type MissingSlotInfo,
	NURSE_SLOT_HOURS_LINE_EXTRA_PX,
	ROLE_HEADER_TOP_GAP_PX,
	ROW_HEIGHT_BY_KIND,
	TOTAL_WIDTH,
	WEEKLY_GAP_VIEW_ROOT_ID,
} from "./consts";
import { buildFlatRows, type FlatRow, FlatRowRenderer, nurseSlotRowNeedsHoursLine } from "./FlatRowRenderer";
import { StickyHeaderOverlay } from "./StickyHeaderOverlay";
import type { NurseSlot } from "./useWeeklyGapView";
import { useWeeklyGapView } from "./useWeeklyGapView";

type WeeklyGapViewProps = {
	searchText?: string | null;
	onSetSearch: (name: string) => void;
	selectedContractSections: ReadonlySet<string>;
};

export const WeeklyGapView: FC<WeeklyGapViewProps> = ({ searchText, onSetSearch, selectedContractSections }) => {
	const ctx = useWeekViewContext();
	const isDynamicWeekStartEnabled = useFeatureFlag(FEATURE_FLAGS.StaffingDynamicWeekStart);
	const weekViewData = isDynamicWeekStartEnabled ? ctx.visibleWeekViewData : ctx.weekViewData;
	const { settings } = useUserSettingsContext();
	const groupingType = settings.weeklyGroupingType;
	const navigate = useNavigate();
	const { chosenDepartment } = useHospitalData();
	const formatPersonName = useFormatPersonName();
	// The names the Search Staff options list, from the same people, so a picked name always finds its rows.
	const searchNameOf = useCallback(
		(nurseId: number): string => {
			const person = ctx.getSchedulePerson(nurseId);
			// DEFAULT: someone the weeks don't name has no name to search by, so no search matches them.
			return person ? formatPersonName(person) : "";
		},
		[ctx.getSchedulePerson, formatPersonName],
	);
	const usePostShiftTab = useFeatureFlag(FEATURE_FLAGS.PickupByLocation);

	const { roleSections, dataProblems } = useWeeklyGapView(weekViewData, groupingType, selectedContractSections);
	const { trackError } = useAnalytics();
	useEffect(() => {
		for (const { event, properties } of dataProblems) trackError(event, properties);
	}, [dataProblems, trackError]);

	const filteredRoleSections = useMemo(() => {
		if (!searchText) return roleSections;

		return roleSections
			.map((section) => {
				const filteredTimeTitles = section.timeTitles
					.map((title) => {
						const filteredLocations = title.locations.filter((cl) =>
							cl.dayData.some((day) => day.nurses.some((n) => searchNameOf(n.nurseInShift.nurseId) === searchText)),
						);
						if (filteredLocations.length === 0) return null;
						return { ...title, locations: filteredLocations };
					})
					.filter((t): t is NonNullable<typeof t> => t !== null);

				if (filteredTimeTitles.length === 0) return null;
				return { ...section, timeTitles: filteredTimeTitles };
			})
			.filter((s): s is NonNullable<typeof s> => s !== null);
	}, [roleSections, searchText, searchNameOf]);

	const searchedNurseId = useMemo(() => {
		if (!searchText) return null;
		return [...weekPersonIdsOf(weekViewData)].find((nurseId) => searchNameOf(nurseId) === searchText) ?? null;
	}, [searchText, weekViewData, searchNameOf]);

	const [expandedTitles, setExpandedTitles] = useState<Set<string>>(new Set());

	useEffect(() => {
		if (!searchText) return;
		const allKeys = new Set<string>();
		for (const section of filteredRoleSections) {
			for (const title of section.timeTitles) {
				allKeys.add(`${section.shortRole}__${title.label}`);
			}
		}
		setExpandedTitles(allKeys);
	}, [searchText, filteredRoleSections]);

	const [nurseMenuState, setNurseMenuState] = useState<{ nurseInShift: NurseInShift; anchorEl: HTMLElement; isActual: boolean } | null>(null);
	const [addNurseState, setAddNurseState] = useState<MissingSlotInfo | null>(null);
	const [topRowIndex, setTopRowIndex] = useState<number>(0);

	const toggleTitle = useCallback((key: string) => {
		setExpandedTitles((prev) => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});
	}, []);

	const handleCollapseRole = useCallback((keys: string[]) => {
		setExpandedTitles((prev) => {
			const next = new Set(prev);
			for (const k of keys) next.delete(k);
			return next;
		});
	}, []);

	const handleNurseClick = useCallback((nurse: NurseSlot, anchor: HTMLElement) => {
		setNurseMenuState({ nurseInShift: nurse.nurseInShift, anchorEl: anchor, isActual: nurse.isActual });
	}, []);

	const handleNurseDoubleClick = useCallback(
		(nurseId: number) => {
			const name = searchNameOf(nurseId);
			if (name) onSetSearch(name);
		},
		[onSetSearch, searchNameOf],
	);

	const handleMissingClick = useCallback(
		(info: MissingSlotInfo) => {
			if (info.existingReqId) {
				const searchParams = new URLSearchParams(window.location.search);
				searchParams.set("requestId", info.existingReqId);
				navigate(`${window.location.pathname}?${searchParams.toString()}`, { replace: true });
				return;
			}
			setAddNurseState(info);
		},
		[navigate],
	);

	const getAdjustedLockedTimes = useCallback(() => {
		if (!addNurseState) return undefined;
		return slotWindowOnDay(addNurseState, addNurseState.dayDate, chosenDepartment.dayStart);
	}, [addNurseState, chosenDepartment.dayStart]);

	const flatRows = useMemo<FlatRow[]>(
		() =>
			buildFlatRows({
				sections: filteredRoleSections,
				expandedTitles,
				roleGroups: chosenDepartment.role_groups,
			}),
		[filteredRoleSections, expandedTitles, chosenDepartment.role_groups],
	);

	const rowHeight = useCallback(
		(index: number) => {
			const row = flatRows[index];
			const base = ROW_HEIGHT_BY_KIND[row.kind];
			if (row.kind === "role-header" && !row.isFirst) return base + ROLE_HEADER_TOP_GAP_PX;
			if (nurseSlotRowNeedsHoursLine(row)) return base + NURSE_SLOT_HOURS_LINE_EXTRA_PX;
			return base;
		},
		[flatRows],
	);

	const renderItem = useCallback(
		(row: FlatRow) => (
			<FlatRowRenderer
				row={row}
				weekViewData={weekViewData}
				searchedNurseId={searchedNurseId}
				roleGroups={chosenDepartment.role_groups}
				onToggleTitle={toggleTitle}
				onCollapseRole={handleCollapseRole}
				onNurseClick={handleNurseClick}
				onNurseDoubleClick={handleNurseDoubleClick}
				onMissingClick={handleMissingClick}
			/>
		),
		[
			weekViewData,
			searchedNurseId,
			chosenDepartment.role_groups,
			toggleTitle,
			handleCollapseRole,
			handleNurseClick,
			handleNurseDoubleClick,
			handleMissingClick,
		],
	);

	const handleRowsRendered = useCallback(({ startIndex }: { startIndex: number; stopIndex: number }) => {
		setTopRowIndex(startIndex);
	}, []);

	if (filteredRoleSections.length === 0) return null;

	return (
		<Box id={WEEKLY_GAP_VIEW_ROOT_ID} data-testid="weekly-gap-view-root" sx={{ bgcolor: "white", position: "relative", width: TOTAL_WIDTH, height: "100%" }}>
			<StickyHeaderOverlay
				flatRows={flatRows}
				startIndex={topRowIndex}
				weekViewData={weekViewData}
				roleGroups={chosenDepartment.role_groups}
				onToggleTitle={toggleTitle}
				onCollapseRole={handleCollapseRole}
			/>
			<VirtualizedList
				items={flatRows}
				rowHeight={rowHeight}
				renderItem={renderItem}
				overscanCount={2}
				onRowsRendered={handleRowsRendered}
				sx={{ width: TOTAL_WIDTH, height: "100%" }}
			/>

			{nurseMenuState && (
				<NormalizedNurseMenu
					nurseInShift={nurseMenuState.nurseInShift}
					menuAnchorElement={nurseMenuState.anchorEl}
					source={ChangesSource.Staffing}
					onClose={() => setNurseMenuState(null)}
					// A punched shift acts on the actual row, the same way the Day view's actual table does.
					showActual={nurseMenuState.isActual}
				/>
			)}

			{/* Opened on the gap's day: the panel opens on its first shift; a gap with hours locks them in on top. */}
			{addNurseState && (
				<StaffingChanges
					setIsAdd={() => setAddNurseState(null)}
					date={addNurseState.dayDate}
					rolesToDisplay={addNurseState.roles}
					onClose={() => setAddNurseState(null)}
					defaultTab={usePostShiftTab ? "create-shift" : "add-nurse"}
					defaultRoom={addNurseState.locationName}
					defaultJobId={addNurseState.jobId}
					lockedTimes={getAdjustedLockedTimes()}
					// A gap carries its hours, so the shift is decided; without them Add Staff would ask first.
					initialShiftSelected={getAdjustedLockedTimes() !== undefined}
					hideRecommendations={true}
				/>
			)}
		</Box>
	);
};
